import { useEffect, useRef } from 'react'
import { notification } from 'antd'

type NotificationCenterProps = {
  id?: string
  count: number
  enabled?: boolean
  message?: string | ((_count: number) => string)
  description?: string
  link?: string
  browser?: boolean
  className?: string
  onOpen?: (_count: number) => void
  onDismiss?: (_count: number) => void
}

export default function NotificationCenter({
  id = 'notification-center',
  count,
  enabled = true,
  message,
  description,
  link,
  browser = true,
  className,
  onOpen,
  onDismiss,
}: NotificationCenterProps) {
  const key = `notif-${id}`
  const lastDismissedCount = useRef<number | null>(null)
  const lastNotifiedCount = useRef<number>(0)
  const activeBrowserNotif = useRef<Notification | null>(null)

  // in-app notification
  useEffect(() => {
    if (!enabled) {
      notification.destroy(key)
      return
    }

    if (count === 0) {
      notification.destroy(key)
      lastDismissedCount.current = null
      return
    }

    if (lastDismissedCount.current === count) {
      return
    }

    const resolvedMessage =
      typeof message === 'function'
        ? message(count)
        : message || `${count} notification${count > 1 ? 's' : ''}`

    notification.open({
      key,
      message: resolvedMessage,
      description,
      duration: 0,
      placement: 'topRight',
      className,
      style: {
        backgroundColor: '#333',
        border: '4px solid red',
      },
      onClick: () => {
        if (link) window.open(link, '_blank')
        notification.destroy(key)
        lastDismissedCount.current = count
        onOpen?.(count)
        onDismiss?.(count)
      },
    })

    return () => {
      notification.destroy(key)
    }
  }, [
    count,
    enabled,
    message,
    description,
    link,
    className,
    onOpen,
    onDismiss,
    key,
  ])

  // browser (native) notification
  useEffect(() => {
    if (
      !browser ||
      typeof window === 'undefined' ||
      typeof Notification === 'undefined'
    )
      return

    // close if no unread or dismissed for this count
    if (count === 0 || lastDismissedCount.current === count) {
      if (activeBrowserNotif.current) {
        try {
          activeBrowserNotif.current.close()
        } catch (err) {
          console.error('Failed to close active browser notification', err)
        }
        activeBrowserNotif.current = null
      }
      lastNotifiedCount.current = 0
      return
    }

    if (lastNotifiedCount.current === count) return

    let mounted = true
    ;(async () => {
      try {
        let permission = Notification.permission
        if (permission === 'default')
          permission = await Notification.requestPermission()
        if (permission !== 'granted' || !mounted) return

        // close previous
        if (activeBrowserNotif.current) {
          try {
            activeBrowserNotif.current.close()
          } catch (err) {
            console.error('Failed to close active browser notification', err)
          }
        }

        const title =
          typeof message === 'function'
            ? message(count)
            : message || `${count} notification${count > 1 ? 's' : ''}`
        const n = new Notification(title, {
          body: description,
          tag: `app-notif-${id}`,
          data: { url: link },
          requireInteraction: false,
        })

        activeBrowserNotif.current = n
        lastNotifiedCount.current = count

        n.onclick = (ev) => {
          ev.preventDefault()
          if (link) {
            window.open(link, '_blank')
            window.focus?.()
          }
          try {
            n.close()
          } catch (err) {
            console.error('Failed to close notification on click', err)
          }
          lastDismissedCount.current = count
          onOpen?.(count)
          onDismiss?.(count)
        }

        // auto close after 10s
        setTimeout(() => {
          try {
            n.close()
          } catch (err) {
            console.error('Failed to auto-close notification', err)
          }
          if (activeBrowserNotif.current === n)
            activeBrowserNotif.current = null
        }, 10000)
      } catch (err) {
        console.error('NotificationCenter: browser notify failed', err)
      }
    })()

    return () => {
      mounted = false
      if (activeBrowserNotif.current) {
        try {
          activeBrowserNotif.current.close()
        } catch {
          // nothing
        }
        activeBrowserNotif.current = null
      }
    }
  }, [count, browser, message, description, link, id, onOpen, onDismiss])

  return null
}
